import React, { useState, useEffect } from "react";
import { FiUser, FiMenu, FiX, FiLogOut, FiLogIn, FiClock, FiHome } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import AuthModal from "./AuthModal";
import logo from "../assets/images/logo.png";
import { useNavigate, useLocation, Link } from "react-router-dom";

const Header = () => {
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Détecter la taille de l'écran
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Effet pour détecter le scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    if (isMobile) {
      window.addEventListener("scroll", handleScroll);
    }
    
    return () => {
      if (isMobile) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isMobile]);

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const handleLogout = async () => {
    await logout();
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    navigate("/");
  };

  // Vérifier les pages
  const showHistoryButton = user && location.pathname !== "/history";
  const isHomePage = location.pathname === "/";
  const isHistoryPage = location.pathname === "/history";

  // Fermer dropdown si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".dropdown-container")) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [dropdownOpen]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled && isMobile
          ? "bg-white shadow-lg border-b border-gray-200" 
          : "bg-white border-b border-gray-200"
      }`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          
          {/* Menu burger mobile - Gauche */}
          <div className="md:hidden flex items-center">
            <button
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>

          {/* Logo - Centré sur mobile, à gauche sur desktop */}
          <div className="absolute left-1/2 transform -translate-x-1/2 md:relative md:left-auto md:transform-none md:flex-1">
            <Link to="/" className="flex items-center">
              <img 
                src={logo} 
                alt="sumdocx" 
                className="h-10 md:h-12 w-auto transition-all duration-300" 
              />
            </Link>
          </div>

          {/* Navigation desktop - Droite */}
          <div className="hidden md:flex items-center gap-6">
            <nav className="flex items-center gap-4 mr-6">
              <Link
                to="/"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                  isHomePage 
                    ? "bg-teal-50 text-teal-600 font-medium" 
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <FiHome size={18} />
                <span>Accueil</span>
              </Link>
              
              {user && (
                <Link
                  to="/history"
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                    isHistoryPage 
                      ? "bg-teal-50 text-teal-600 font-medium" 
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FiClock size={18} />
                  <span>Historique</span>
                </Link>
              )}
            </nav>

            {/* User section desktop */}
            <div className="flex items-center">
              {user ? (
                <div className="dropdown-container relative">
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  >
                    <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center border-2 border-teal-500/20">
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={user.displayName || "User"} 
                          className="w-full h-full rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <FiUser size={20} className="text-teal-600" />
                      )}
                    </div>
                    <div className="text-left hidden lg:block">
                      <p className="font-medium text-sm text-gray-800">
                        {user.displayName || user.email?.split('@')[0] || "Utilisateur"}
                      </p>
                      <p className="text-xs text-gray-500 truncate max-w-[150px]">
                        {user.email}
                      </p>
                    </div>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white text-gray-800 rounded-xl shadow-2xl z-50 overflow-hidden border border-gray-200">
                      <div className="p-4 border-b border-gray-100 bg-gray-50">
                        <p className="font-semibold text-gray-900">{user.displayName || "Utilisateur"}</p>
                        <p className="text-sm text-gray-600 truncate mt-1">{user.email}</p>
                      </div>
                      
                      <div className="p-2">
                        <Link
                          to="/history"
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <FiClock size={18} className="text-teal-600" />
                          <span>Historique</span>
                        </Link>
                        
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors mt-1"
                        >
                          <FiLogOut size={18} />
                          <span>Déconnexion</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setAuthModalOpen(true)}
                  className="px-5 py-2.5 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <span className="flex items-center gap-2">
                    <FiLogIn size={18} />
                    Connexion
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Mobile user indicator - Droite */}
          <div className="md:hidden flex items-center">
            {user ? (
              <div className="dropdown-container">
                <button
                  onClick={toggleDropdown}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  {user.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt="User" 
                      className="w-8 h-8 rounded-full"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <FiUser size={18} className="text-teal-600" />
                    </div>
                  )}
                </button>
                
                {dropdownOpen && (
                  <div className="absolute right-4 top-16 w-56 bg-white text-gray-800 rounded-xl shadow-2xl z-50 overflow-hidden border border-gray-200">
                    <div className="p-4 border-b border-gray-100 bg-gray-50">
                      <p className="font-semibold text-gray-900">{user.displayName || "Utilisateur"}</p>
                      <p className="text-sm text-gray-600 truncate">{user.email}</p>
                    </div>
                    
                    <div className="p-2">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                      >
                        <FiLogOut size={18} />
                        <span>Déconnexion</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => setAuthModalOpen(true)}
                className="p-2.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Se connecter"
              >
                <FiLogIn size={22} className="text-gray-700" />
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <div className="fixed top-0 left-0 h-full w-64 bg-white text-gray-800 shadow-2xl z-50 transform transition-transform duration-300">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <img src={logo} alt="sumdocx" className="h-10" />
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-lg hover:bg-gray-100"
                  >
                    <FiX size={24} className="text-gray-600" />
                  </button>
                </div>
                
                {user && (
                  <div className="mb-6 p-4 bg-teal-50 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        {user.photoURL ? (
                          <img 
                            src={user.photoURL} 
                            alt="User" 
                            className="w-full h-full rounded-full"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <FiUser size={20} className="text-teal-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{user.displayName || "Utilisateur"}</p>
                        <p className="text-sm text-gray-600 truncate">{user.email}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-4">
                <nav className="space-y-2">
                  <Link
                    to="/"
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isHomePage 
                        ? "bg-teal-50 text-teal-600 font-medium" 
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FiHome size={20} />
                    <span>Accueil</span>
                  </Link>
                  
                  {user && (
                    <Link
                      to="/history"
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                        isHistoryPage 
                          ? "bg-teal-50 text-teal-600 font-medium" 
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <FiClock size={20} />
                      <span>Historique</span>
                    </Link>
                  )}
                </nav>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  {user ? (
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-3 py-3 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                    >
                      <FiLogOut size={20} />
                      <span>Déconnexion</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setAuthModalOpen(true);
                        setMobileMenuOpen(false);
                      }}
                      className="w-full px-4 py-3 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors"
                    >
                      Connexion
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Auth Modal */}
      {authModalOpen && <AuthModal closeModal={() => setAuthModalOpen(false)} />}

      {/* Floating History Button - Amélioré */}
      {showHistoryButton && (
        <Link
          to="/history"
          className="
            fixed bottom-8 right-8 
            bg-teal-500 hover:bg-teal-600 
            text-white 
            w-16 h-16 
            rounded-full 
            flex items-center justify-center 
            shadow-2xl shadow-teal-400/50 
            transition-all duration-300 
            hover:scale-110 
            active:scale-95
            z-40
            border-2 border-white/20
            animate-pulse-subtle
          "
          aria-label="Voir l'historique"
        >
          <div className="relative">
            <FiClock size={28} />
          </div>
        </Link>
      )}

      {/* Animation CSS pour le bouton flottant */}
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s infinite;
        }
      `}</style>
    </>
  );
};

export default Header;